/**
 * Recursively removes undefined values from an object or array.
 * This is useful for Firestore operations which don't accept undefined values.
 * 
 * @param value - The value to clean (object, array, or primitive)
 * @returns The cleaned value with all undefined values removed
 */
export function removeUndefinedValues<T>(value: T): T {
  // Handle null values
  if (value === null) {
    return value;
  }

  // Handle arrays - recursively clean each element
  if (Array.isArray(value)) {
    return value.map(removeUndefinedValues) as T;
  }

  // Handle Date objects - return as-is
  if (value instanceof Date) {
    return value;
  }

  // Handle objects - recursively clean properties
  if (typeof value === "object") {
    const cleaned = {} as T;
    
    for (const [key, val] of Object.entries(value)) {
      // Skip undefined values
      if (val === undefined) {
        continue;
      }
      
      // Recursively clean nested values
      (cleaned as Record<string, unknown>)[key] = removeUndefinedValues(val);
    }
    
    return cleaned;
  }

  // Return primitives as-is
  return value;
}
